import { createTheme, type Theme } from '@mui/material/styles';
import { C } from './tokens';

/**
 * Repaginação dos cadastros em etapas SEM trocar os componentes: o react-admin e o MUI
 * continuam desenhando a tela, só com outros valores. Vale para tudo dentro de
 * `<ThemeProvider theme={wizardTheme}>` — hoje, a campanha de recuperação.
 *
 * Mora aqui, e não em `sx` espalhado pelos passos, porque o acerto é o mesmo em ~30 campos:
 * repetido no ponto de uso, ele diverge no primeiro campo que alguém adicionar depois.
 */
export const wizardTheme = (outer: Theme) => createTheme(outer, {
  components: {
    // O tema do admin manda `filled` em todo TextField (defaultLightTheme do react-admin).
    // A caixa do wizard é branca com contorno, como no cadastro de desconto v2.
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const, size: 'small' as const, margin: 'none' as const },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: 8,
          fontSize: 15,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.inputBorder },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.ink },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.violet, borderWidth: 2 },
          '&.Mui-disabled': { backgroundColor: '#f1f5f9' },
        },
        input: { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: C.muted, '&.Mui-focused': { color: C.violet } },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: 12.5, color: C.quiet, marginLeft: 2, lineHeight: 1.45 } },
    },
    // O tema do admin pinta TODO Select de laranja (#e65017); dentro do wizard o texto é tinta.
    MuiSelect: {
      styleOverrides: { root: { color: C.ink } },
    },
    MuiPaper: {
      styleOverrides: { outlined: { borderColor: C.line, borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
        outlined: { borderColor: C.line },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: C.lineSoft } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10, alignItems: 'flex-start' } },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const, borderRadius: 8, fontWeight: 600 },
        // Verde da marca com texto branco não passa no contraste (4,5:1): o texto é tinta.
        containedPrimary: {
          color: C.ink,
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': { backgroundColor: C.greenHover, boxShadow: '0 6px 16px rgba(39,80,48,.18)' },
        },
        outlinedPrimary: { borderColor: C.violet, color: C.violet },
        textPrimary: { color: C.violet },
      },
    },
    // Alternador de duas opções (o "O que o nível dá", a unidade do requisito).
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontSize: 14,
          fontWeight: 500,
          color: C.ink,
          borderColor: C.inputBorder,
          padding: '0 18px',
          height: 40,
          whiteSpace: 'nowrap' as const,
          // Verde da marca com texto escuro — branco sobre #79CF62 fica em ~1,8:1.
          '&.Mui-selected': {
            backgroundColor: C.green,
            color: C.ink,
            fontWeight: 700,
            '&:hover': { backgroundColor: C.greenHover },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: { root: { backgroundColor: '#fff', borderRadius: 8 } },
    },
  },
});
